/* Trail Crashers motion: content is visible until an observer takes ownership. */
(() => {
  'use strict';

  window.TrailCrashersMotion?.destroy();

  const sectionSelector = '[data-tc-motion-section]';
  const revealSelector = '[data-tc-reveal]';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 990px) and (pointer: fine)');
  const managed = new Set();
  const pending = new Set();
  const revealQueue = new Set();
  const transitions = new Map();
  const heroes = new Map();
  const listeners = [];
  let revealObserver;
  let heroObserver;
  let settingsObserver;
  let revealFrame = 0;
  let restoreFrame = 0;
  let scrollFrame = 0;
  let started = false;
  let destroyed = false;

  const mode = () => document.body?.dataset.tcMotion || 'full';
  const editor = () => Boolean(window.Shopify?.designMode);
  const entrances = () => Boolean(revealObserver) && !reduced.matches && mode() !== 'off' && !editor();
  const parallax = () => entrances() && mode() === 'full' && desktop.matches && !document.hidden;

  function listen(target, event, callback, options) {
    target.addEventListener(event, callback, options);
    listeners.push(() => target.removeEventListener(event, callback, options));
  }

  function watch(query, callback) {
    if (query.addEventListener) listen(query, 'change', callback);
    else {
      query.addListener(callback);
      listeners.push(() => query.removeListener(callback));
    }
  }

  function restoreTransitions() {
    restoreFrame = 0;
    transitions.forEach(([value, priority], element) => {
      if (value) element.style.setProperty('transition', value, priority);
      else element.style.removeProperty('transition');
    });
    transitions.clear();
  }

  function reveal(element, immediate = false) {
    if (!pending.has(element) && (!immediate || !managed.has(element))) return;
    revealQueue.delete(element);
    pending.delete(element);
    revealObserver?.unobserve(element);
    if (immediate) {
      // Focus or editor selection can arrive during an entrance's delay or transition.
      // Finish both the observed wrapper and its separately animated decoration.
      [element, ...element.querySelectorAll('.tc-hero__line, .tc-ticker > span > span')].forEach((target) => {
        if (!transitions.has(target)) {
          transitions.set(target, [target.style.getPropertyValue('transition'), target.style.getPropertyPriority('transition')]);
        }
        target.style.setProperty('transition', 'none', 'important');
      });
      if (!restoreFrame) {
        restoreFrame = requestAnimationFrame(() => {
          restoreFrame = requestAnimationFrame(restoreTransitions);
        });
      }
    }
    element.classList.remove('tc-motion-pending');
    element.classList.add('tc-motion-visible');
  }

  function queueReveal(element) {
    revealQueue.add(element);
    if (revealFrame) return;
    // Let the observer's initial state paint before changing the reveal state.
    revealFrame = requestAnimationFrame(() => {
      revealFrame = 0;
      revealQueue.forEach((candidate) => reveal(candidate));
      revealQueue.clear();
    });
  }

  function showWithin(root) {
    managed.forEach((element) => {
      if (root === element || root.contains(element) || element.contains(root)) reveal(element, true);
    });
  }

  function resetParallax() {
    if (scrollFrame) cancelAnimationFrame(scrollFrame);
    scrollFrame = 0;
    heroes.forEach((hero, image) => image.style.removeProperty('--tc-parallax-y'));
  }

  function updateParallax() {
    scrollFrame = 0;
    if (!parallax()) return;
    const viewport = window.innerHeight;
    heroes.forEach((hero, image) => {
      if (!hero.active || !image.isConnected) return;
      const box = hero.root.getBoundingClientRect();
      const progress = (viewport / 2 - (box.top + box.height / 2)) / ((viewport + box.height) / 2);
      // A small bounded translation keeps the photograph inside its crop.
      const offset = Math.max(-24, Math.min(24, progress * 24));
      image.style.setProperty('--tc-parallax-y', `${offset.toFixed(2)}px`);
    });
  }

  function scheduleParallax() {
    if (scrollFrame || !parallax() || !Array.from(heroes.values()).some((hero) => hero.active)) return;
    scrollFrame = requestAnimationFrame(updateParallax);
  }

  function findWithin(root, selector) {
    const elements = Array.from(root.querySelectorAll(selector));
    if (root.matches?.(selector)) elements.unshift(root);
    return elements;
  }

  function refresh(root = document) {
    if (destroyed || !started || !root.querySelectorAll) return;
    const enabled = entrances();
    document.body.classList.toggle('tc-motion-ready', enabled);
    if (!enabled) pending.forEach((element) => reveal(element, true));

    findWithin(root, revealSelector).forEach((element) => {
      if (!element.closest(sectionSelector) || managed.has(element)) return;
      managed.add(element);
      if (!enabled) return;
      // Observe first: a failed enhancement must never leave content hidden.
      try {
        revealObserver.observe(element);
        element.style.setProperty('--tc-reveal-delay', `${Math.max(0, Math.min(700, Number(element.dataset.tcDelay) || 0))}ms`);
        pending.add(element);
        element.classList.add('tc-motion-pending');
        if (element.contains(document.activeElement)) reveal(element, true);
      } catch {
        element.classList.remove('tc-motion-pending');
      }
    });

    if (heroObserver) {
      findWithin(root, '.tc-hero__image').forEach((image) => {
        const section = image.closest(sectionSelector);
        if (!section || heroes.has(image)) return;
        heroes.set(image, { root: section, active: false });
        heroObserver.observe(section);
      });
    }
    if (parallax()) scheduleParallax();
    else resetParallax();
  }

  function unload(event) {
    const root = event.target;
    managed.forEach((element) => {
      if (!root.contains(element)) return;
      reveal(element, true);
      element.classList.remove('tc-motion-visible');
      element.style.removeProperty('--tc-reveal-delay');
      managed.delete(element);
    });
    heroes.forEach((hero, image) => {
      if (!root.contains(image)) return;
      heroObserver?.unobserve(hero.root);
      image.style.removeProperty('--tc-parallax-y');
      heroes.delete(image);
    });
  }

  function visibility() {
    if (document.hidden) resetParallax();
    else scheduleParallax();
  }

  function start() {
    if (started || destroyed || !document.body) return;
    started = true;
    if ('IntersectionObserver' in window) {
      try {
        revealObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            // isIntersecting also covers unusually tall sections whose ratio stays small.
            if (entry.isIntersecting) queueReveal(entry.target);
          });
        }, { threshold: 0.08, rootMargin: '0px 0px -24px 0px' });
        heroObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            heroes.forEach((hero) => {
              if (hero.root === entry.target) hero.active = entry.isIntersecting;
            });
          });
          scheduleParallax();
        });
      } catch {
        revealObserver?.disconnect();
        revealObserver = null;
      }
    }

    listen(document, 'focusin', (event) => {
      managed.forEach((element) => {
        if (element.contains(event.target)) reveal(element, true);
      });
    });
    listen(window, 'scroll', scheduleParallax, { passive: true });
    listen(window, 'resize', scheduleParallax, { passive: true });
    listen(document, 'visibilitychange', visibility);
    listen(window, 'pageshow', (event) => {
      // History restores should retain the visitor's position and visible content.
      if (event.persisted) showWithin(document);
      refresh();
    });
    listen(document, 'shopify:section:load', (event) => refresh(event.target));
    listen(document, 'shopify:section:unload', unload);
    listen(document, 'shopify:section:select', (event) => showWithin(event.target));
    listen(document, 'shopify:block:select', (event) => showWithin(event.target));
    watch(reduced, () => refresh());
    watch(desktop, () => {
      if (parallax()) scheduleParallax();
      else resetParallax();
    });
    if ('MutationObserver' in window) {
      settingsObserver = new MutationObserver(() => refresh());
      settingsObserver.observe(document.body, { attributes: true, attributeFilter: ['data-tc-motion'] });
    }
    refresh();
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    document.removeEventListener('DOMContentLoaded', start);
    listeners.forEach((remove) => remove());
    revealObserver?.disconnect();
    heroObserver?.disconnect();
    settingsObserver?.disconnect();
    [revealFrame, restoreFrame, scrollFrame].forEach((frame) => cancelAnimationFrame(frame));
    restoreTransitions();
    resetParallax();
    document.body?.classList.remove('tc-motion-ready');
    managed.forEach((element) => {
      element.classList.remove('tc-motion-pending', 'tc-motion-visible');
      element.style.removeProperty('--tc-reveal-delay');
    });
    pending.clear();
    revealQueue.clear();
    managed.clear();
    heroes.clear();
  }

  window.TrailCrashersMotion = { refresh, destroy };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
